/**
 * Lockout Protection Utility
 *
 * Prevents the system from reaching a state where no active user
 * holds both minimum admin permissions:
 * - manage_users
 * - manage_roles_permissions
 *
 * At least ONE active user must ALWAYS hold BOTH permissions.
 */

import { Role, USER_ROLE_ASSIGNMENTS } from "@/types/permissions";
import { getDemoRoles } from "./demoRolePersistence";

const PROTECTED_PERMISSIONS = [
  "manage_users",
  "manage_roles_permissions",
] as const;

export const LOCKOUT_ERROR_MESSAGE =
  "This change cannot be made. At least one user must always have permission to manage users and manage roles & permissions. Making this change would lock everyone out of system administration. Please ensure another user has these permissions before making this change.";

/**
 * Check if a role has both protected permissions
 */
function roleHasBothProtectedPermissions(role: Role): boolean {
  return PROTECTED_PERMISSIONS.every((perm) =>
    role.permissions.includes(perm as any),
  );
}

/**
 * Get count of users who have both protected permissions
 *
 * In demo mode: Uses USER_ROLE_ASSIGNMENTS and demo roles
 * In live mode: Would use actual user data from API
 */
export function countUsersWithBothProtectedPermissions(
  roles: Role[],
  mode: "demo" | "live" = "demo",
): number {
  if (mode === "demo") {
    // Count users in demo mode
    let count = 0;

    for (const userAssignment of USER_ROLE_ASSIGNMENTS) {
      const userRole = roles.find((r) => r.id === userAssignment.roleId);
      if (userRole && roleHasBothProtectedPermissions(userRole)) {
        count++;
      }
    }

    return count;
  }

  // Live mode would use actual API data
  // For now, return a safe value
  return 1;
}

/**
 * TRIGGER 1: Check if saving role permission changes would cause lockout
 *
 * @param updatedRole - The role being saved with new permissions
 * @param mode - 'demo' or 'live'
 * @returns true if the change would cause lockout, false if safe
 */
export function wouldRoleUpdateCauseLockout(
  updatedRole: Role,
  mode: "demo" | "live" = "demo",
): boolean {
  const roles = mode === "demo" ? getDemoRoles() : [];

  // Create a hypothetical roles array with the updated role
  const rolesAfterUpdate = roles.map((r) =>
    r.id === updatedRole.id ? updatedRole : r,
  );

  // Check if any users would still have both permissions
  const usersWithBothPermissions = countUsersWithBothProtectedPermissions(
    rolesAfterUpdate,
    mode,
  );

  return usersWithBothPermissions === 0;
}

/**
 * TRIGGER 2: Check if changing a user's role would cause lockout
 *
 * @param userId - The user whose role is being changed
 * @param newRoleId - The new role ID being assigned
 * @param mode - 'demo' or 'live'
 * @returns true if the change would cause lockout, false if safe
 */
export function wouldUserRoleChangeCauseLockout(
  userId: string,
  newRoleId: string,
  mode: "demo" | "live" = "demo",
): boolean {
  if (mode === "demo") {
    const roles = getDemoRoles();

    // Find the user's current role
    const userAssignment = USER_ROLE_ASSIGNMENTS.find(
      (u) => u.userId === userId,
    );
    if (!userAssignment) return false;

    const currentRole = roles.find((r) => r.id === userAssignment.roleId);
    const newRole = roles.find((r) => r.id === newRoleId);

    // If current role doesn't have both permissions, this change is safe
    if (!currentRole || !roleHasBothProtectedPermissions(currentRole)) {
      return false;
    }

    // If new role has both permissions, this change is safe
    if (newRole && roleHasBothProtectedPermissions(newRole)) {
      return false;
    }

    // This user currently has both permissions and would lose them
    // Check if any OTHER users have both permissions
    let otherUsersWithBothPermissions = 0;

    for (const otherUser of USER_ROLE_ASSIGNMENTS) {
      if (otherUser.userId === userId) continue; // Skip the user being changed

      const otherUserRole = roles.find((r) => r.id === otherUser.roleId);
      if (otherUserRole && roleHasBothProtectedPermissions(otherUserRole)) {
        otherUsersWithBothPermissions++;
      }
    }

    return otherUsersWithBothPermissions === 0;
  }

  // Live mode would use actual API data
  return false;
}

/**
 * TRIGGER 3: Check if deleting a role would cause lockout
 *
 * @param roleId - The role ID being deleted
 * @param mode - 'demo' or 'live'
 * @returns true if the deletion would cause lockout, false if safe
 */
export function wouldRoleDeletionCauseLockout(
  roleId: string,
  mode: "demo" | "live" = "demo",
): boolean {
  if (mode === "demo") {
    const roles = getDemoRoles();
    const roleToDelete = roles.find((r) => r.id === roleId);

    // If the role doesn't have both permissions, deletion is safe
    if (!roleToDelete || !roleHasBothProtectedPermissions(roleToDelete)) {
      return false;
    }

    // This role has both permissions - check if users assigned to it
    // are the ONLY users with both permissions
    const usersInRoleToDelete = USER_ROLE_ASSIGNMENTS.filter(
      (u) => u.roleId === roleId,
    );

    if (usersInRoleToDelete.length === 0) {
      // No users in this role, safe to delete
      return false;
    }

    // Check if any users in OTHER roles have both permissions
    let usersInOtherRolesWithBothPermissions = 0;

    for (const userAssignment of USER_ROLE_ASSIGNMENTS) {
      if (userAssignment.roleId === roleId) continue; // Skip users in role being deleted

      const userRole = roles.find((r) => r.id === userAssignment.roleId);
      if (userRole && roleHasBothProtectedPermissions(userRole)) {
        usersInOtherRolesWithBothPermissions++;
      }
    }

    return usersInOtherRolesWithBothPermissions === 0;
  }

  // Live mode would use actual API data
  return false;
}
